import React, { useEffect, useMemo, useState } from "react";
import "./UserCalendar.css";

const API_BASE =
    process.env.REACT_APP_API_BASE ??
    (process.env.NODE_ENV === "production" ? "" : "http://localhost:5000");

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const RESPONSE_LIST_OPTIONS = [
    { value: "attend", label: "Confirmed List", title: "Confirmed List", empty: "No confirmed members yet." },
    { value: "maybe", label: "Tentative List", title: "Tentative List", empty: "No tentative members yet." },
    { value: "decline", label: "Not Going List", title: "Not Going List", empty: "No not going members yet." },
];

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const MIN_YEAR = 2026;
const MAX_YEAR = new Date().getFullYear() + 5;

/* ================= HELPERS ================= */

const parseDateOnly = (dateStr) => {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split("T")[0].split("-").map(Number);
    return new Date(y, m - 1, d);
};

const isPastEventDate = (dateStr) => {
    const eventDate = parseDateOnly(dateStr);
    if (!eventDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);

    return eventDate < today;
};

function formatFullDate(dateStr) {
    const d = parseDateOnly(dateStr);
    if (!d) return "";

    return d.toLocaleDateString("en-US", {
        month: "long",
        day: "2-digit",
        year: "numeric",
    });
}

function formatBirthSurviveDate(ev) {
    return ev?.birthsurvive || ev?.description || formatFullDate(ev?.date);
}

function formatTime12h(time) {
    if (!time) return "";
    const [h, m] = time.split(":").map(Number);
    const hour = h % 12 || 12;
    const ampm = h >= 12 ? "PM" : "AM";
    return `${hour}:${m.toString().padStart(2, "0")} ${ampm}`;
}

function getWeekday(year, month, day) {
    return new Date(year, month, day).toLocaleDateString("en-US", {
        weekday: "short",
    });
}

/* ================= COMPONENT ================= */

export default function UserCalendar() {
    const today = new Date();

    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth());
    const [events, setEvents] = useState([]);
    const [selectedDay, setSelectedDay] = useState(null);
    const [slideDir, setSlideDir] = useState("next");
    const [pickerOpen, setPickerOpen] = useState(false);

    const [reminders, setReminders] = useState([]);
    const [showReminderPopup, setShowReminderPopup] = useState(false);
    const [responseDrafts, setResponseDrafts] = useState({});
    const [responseMsg, setResponseMsg] = useState("");
    const [eventResponses, setEventResponses] = useState({});
    const [responseListViews, setResponseListViews] = useState({});
    const [savedResponses, setSavedResponses] = useState({});
    const [editingResponses, setEditingResponses] = useState({});

    const canRespondToEvent = (ev) => ["event", "volunteer", "occasion"].includes(ev?.type);
    const isResponseClosed = (ev) => isPastEventDate(ev?.date);

    const responseCounts = (ev) => ({
        attend: Number(ev.attending_count || 0),
        maybe: Number(ev.maybe_count || 0),
        decline: Number(ev.declined_count || 0),
    });

    const updateEventCounts = (eventId, counts) => {
        setEvents((prev) =>
            prev.map((ev) => {
                if (ev.id !== eventId) return ev;

                return {
                    ...ev,
                    attending_count: counts?.attending_count ?? ev.attending_count,
                    maybe_count: counts?.maybe_count ?? ev.maybe_count,
                    declined_count: counts?.declined_count ?? ev.declined_count,
                };
            })
        );
        setSelectedDay((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                events: prev.events.map((ev) => {
                    if (ev.id !== eventId) return ev;
                    return {
                        ...ev,
                        attending_count: counts?.attending_count ?? ev.attending_count,
                        maybe_count: counts?.maybe_count ?? ev.maybe_count,
                        declined_count: counts?.declined_count ?? ev.declined_count,
                    };
                }),
            };
        });
    };

    const loadEventResponses = async (eventId) => {
        try {
            const res = await fetch(`${API_BASE}/api/calendar/${eventId}/responses`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
            });
            const data = await res.json();
            if (res.ok && data.ok) {
                setEventResponses((prev) => ({ ...prev, [eventId]: data.rows || [] }));
            }
        } catch {
            setEventResponses((prev) => ({ ...prev, [eventId]: [] }));
        }
    };

    const submitResponse = async (ev) => {
        if (isResponseClosed(ev)) {
            setResponseMsg("This event date is done. Responses are closed.");
            return;
        }

        const draft = responseDrafts[ev.id] || {};
        const status = draft.status || "attend";

        try {
            const res = await fetch(`${API_BASE}/api/calendar/${ev.id}/response`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
                },
                body: JSON.stringify({
                    status,
                    comment: "",
                }),
            });
            const data = await res.json();

            if (!res.ok || !data.ok) {
                setResponseMsg(data?.error || "Failed to save response.");
                return;
            }

            updateEventCounts(ev.id, data.counts);
            await loadEventResponses(ev.id);
            setSavedResponses((prev) => ({ ...prev, [ev.id]: true }));
            setEditingResponses((prev) => ({ ...prev, [ev.id]: false }));
            setResponseMsg("Your response has been saved.");
        } catch {
            setResponseMsg("Network error while saving response.");
        }
    };

    const closeSelectedDay = () => {
        setSelectedDay(null);
        setResponseMsg("");
    };

    /* ================= LOAD REMINDERS ================= */
    useEffect(() => {
        fetch(`${API_BASE}/api/calendar/reminders`)
            .then(r => r.json())
            .then(d => {
                if (d.ok && d.rows.length > 0) {
                    setReminders(d.rows);
                    setShowReminderPopup(true);
                }
            })
            .catch(() => { });
    }, []);


    /* ================= LOAD EVENTS ================= */

    useEffect(() => {
        fetch(`${API_BASE}/api/calendar/public?year=${year}`)
            .then((r) => r.json())
            .then((d) => setEvents(d.rows || []))
            .catch(() => setEvents([]));
    }, [year]);

    useEffect(() => {
        if (!responseMsg) return undefined;

        const timer = setTimeout(() => {
            setResponseMsg("");
        }, 3000);

        return () => clearTimeout(timer);
    }, [responseMsg]);

    useEffect(() => {
        if (!selectedDay) return;
        selectedDay.events
            .filter(canRespondToEvent)
            .forEach((ev) => loadEventResponses(ev.id));
    }, [selectedDay]);

    /* ================= MONTH GRID ================= */

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startDay = new Date(year, month, 1).getDay();

    const grid = useMemo(() => {
        const cells = [];
        for (let i = 0; i < startDay; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);
        return cells;
    }, [startDay, daysInMonth]);

    const monthEvents = (day) =>
        events.filter((e) => {
            const d = parseDateOnly(e.date);
            return (
                d &&
                d.getFullYear() === year &&
                d.getMonth() === month &&
                d.getDate() === day
            );
        });

    /* ================= NAVIGATION ================= */

    const canPrev = !(year === MIN_YEAR && month === 0);
    const canNext = !(year === MAX_YEAR && month === 11);

    const goPrev = () => {
        if (!canPrev) return;
        setSlideDir("prev");
        setMonth((m) => (m === 0 ? (setYear((y) => y - 1), 11) : m - 1));
    };

    const goNext = () => {
        if (!canNext) return;
        setSlideDir("next");
        setMonth((m) => (m === 11 ? (setYear((y) => y + 1), 0) : m + 1));
    };

    /* ================= RENDER ================= */

    return (
        <div className="user-calendar">
            {/* HEADER */}
            <div className="calendar-events-header">
                <span className="calendar-icon">📅</span>
                <span className="calendar-title">Calendar Events</span>
            </div>

            {/* TOP NAV */}
            <div className="calendar-top">
                <button className="nav-btn" onClick={goPrev} disabled={!canPrev}>
                    ◀
                </button>

                <div
                    className="calendar-title"
                    onClick={() => setPickerOpen(!pickerOpen)}
                >
                    {MONTHS[month]} {year}
                </div>

                <button className="nav-btn" onClick={goNext} disabled={!canNext}>
                    ▶
                </button>

                {pickerOpen && (
                    <div className="month-picker">
                        <select
                            value={month}
                            onChange={(e) => setMonth(Number(e.target.value))}
                        >
                            {MONTHS.map((m, i) => (
                                <option key={m} value={i}>
                                    {m}
                                </option>
                            ))}
                        </select>

                        <select
                            value={year}
                            onChange={(e) => setYear(Number(e.target.value))}
                        >
                            {Array.from(
                                { length: MAX_YEAR - MIN_YEAR + 1 },
                                (_, i) => MIN_YEAR + i
                            ).map((y) => (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            ))}
                        </select>

                        <button
                            className="btn-primary"
                            onClick={() => setPickerOpen(false)}
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>

            {/* WEEK HEADER */}
            <div className="calendar-week">
                {WEEKDAYS.map((d, i) => (
                    <div
                        key={d}
                        className={`week-day ${i === 0 || i === 6 ? "weekend" : ""
                            }`}
                    >
                        {d}
                    </div>
                ))}
            </div>

            {/* REMINDER POPUP */}
            {showReminderPopup && reminders.length > 0 && (
                <div
                    className="modal-overlay"
                    onClick={() => setShowReminderPopup(false)}
                >
                    <div
                        className="modal-box"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 style={{ color: "darkgoldenrod" }}>
                            ⏰ Upcoming Event Reminder
                        </h3>

                        <div className="modal-events-grid">
                            {reminders.map((ev) => (
                                <div key={ev.id} className="modal-event">
                                    <strong>{ev.title || "Upcoming Event"}</strong>

                                    <div className="muted">
                                        📅 {formatFullDate(ev.date)}
                                    </div>

                                    {ev.start_time && (
                                        <div>
                                            ⏰ {formatTime12h(ev.start_time)}
                                            {ev.end_time &&
                                                ` – ${formatTime12h(ev.end_time)}`}
                                        </div>
                                    )}

                                    {ev.location && <div>📍 {ev.location}</div>}

                                    <div className="reminder-note">
                                        Reminder set {ev.reminder_days} day(s) before
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button
                            className="btn-primary"
                            onClick={() => setShowReminderPopup(false)}
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}

            {/* GRID */}
            <div className={`calendar-grid slide-${slideDir}`}>
                {grid.map((day, i) => {
                    if (!day)
                        return (
                            <div
                                key={i}
                                className="calendar-cell empty"
                            />
                        );

                    const weekdayIndex = (startDay + day - 1) % 7;
                    const isWeekend = weekdayIndex === 0 || weekdayIndex === 6;
                    const isToday =
                        day === today.getDate() &&
                        month === today.getMonth() &&
                        year === today.getFullYear();

                    return (
                        <div
                            key={i}
                            className={`calendar-cell ${isToday ? "today" : ""
                                } ${isWeekend ? "weekend" : ""}`}
                            onClick={() =>
                                monthEvents(day).length &&
                                setSelectedDay({
                                    day,
                                    events: monthEvents(day),
                                })
                            }
                        >
                            <div className="cell-date-row">
                                <span
                                    className={`cell-weekday ${["Sun", "Sat"].includes(
                                        getWeekday(year, month, day)
                                    )
                                            ? "weekend"
                                            : ""
                                        }`}
                                >
                                    {getWeekday(year, month, day)}
                                </span>
                                <span className="cell-date">{day}</span>
                            </div>

                            {monthEvents(day).map((ev) => (
                                <div
                                    key={ev.id}
                                    className={`event-pill ${ev.type}`}
                                >
                                    <div className="event-title">
                                        {ev.title ||
                                            `${ev.firstname ?? ""} ${ev.lastname ?? ""
                                                }`.trim()}
                                    </div>

                                    <div className="event-badge">
                                        {ev.type
                                            .replace("_", " ")
                                            .toUpperCase()}
                                    </div>

                                    {ev.start_time && (
                                        <div className="event-time">
                                            {formatTime12h(ev.start_time)}
                                            {ev.end_time &&
                                                ` – ${formatTime12h(
                                                    ev.end_time
                                                )}`}
                                        </div>
                                    )}

                                    {canRespondToEvent(ev) && (
                                        <div className="event-response-counts">
                                            Confirmed: {responseCounts(ev).attend} | Tentative: {responseCounts(ev).maybe} | Not Going: {responseCounts(ev).decline}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>

            {/* MODAL */}
            {selectedDay && (
                <div
                    className="modal-overlay"
                    onClick={closeSelectedDay}
                >
                    <div
                        className="modal-box"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3>
                            {MONTHS[month]} {selectedDay.day}, {year}
                        </h3>

                        <div className="modal-events-grid">
                            {selectedDay.events.map((ev) => (
                                <div
                                    key={ev.id}
                                    className="modal-event"
                                >
                                    {ev.type === "birth_survive" ? (
                                        <>
                                            <strong className="event-title">
                                                Triskelion Birth
                                            </strong>
                                            <div style={{ fontWeight: 600 }}>
                                                {`${ev.firstname ?? ""} ${ev.lastname ?? ""
                                                    }`.trim()}
                                            </div>
                                            <div className="muted">
                                                {formatBirthSurviveDate(ev)}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <strong className="event-title">
                                                {ev.title ||
                                                    "Untitled Event"}
                                            </strong>
                                            <div className="muted event-type">
                                                {ev.type
                                                    .replace("_", " ")
                                                    .toUpperCase()}
                                            </div>
                                        </>
                                    )}

                                    {ev.start_time && (
                                        <div>
                                            ⏰{" "}
                                            {formatTime12h(ev.start_time)}
                                            {ev.end_time &&
                                                ` – ${formatTime12h(
                                                    ev.end_time
                                                )}`}
                                        </div>
                                    )}

                                    {ev.location && (
                                        <div>📍 {ev.location}</div>
                                    )}
                                    {ev.description && (
                                        <p>{ev.description}</p>
                                    )}

                                    {canRespondToEvent(ev) && (
                                        <>
                                            <div className="event-response-counts modal-counts">
                                                Confirmed: {responseCounts(ev).attend} | Tentative: {responseCounts(ev).maybe} | Not Going: {responseCounts(ev).decline}
                                            </div>

                                            <div className="event-response-form">
                                                <select
                                                    value={responseDrafts[ev.id]?.status || "attend"}
                                                    disabled={isResponseClosed(ev) || (savedResponses[ev.id] && !editingResponses[ev.id])}
                                                    onChange={(e) =>
                                                        setResponseDrafts((prev) => ({
                                                            ...prev,
                                                            [ev.id]: {
                                                                ...(prev[ev.id] || {}),
                                                                status: e.target.value,
                                                            },
                                                        }))
                                                    }
                                                >
                                                    <option value="attend">Confirm</option>
                                                    <option value="maybe">Tentative</option>
                                                    <option value="decline">Not Going</option>
                                                </select>

                                                <button
                                                    className="btn-primary"
                                                    type="button"
                                                    disabled={isResponseClosed(ev)}
                                                    onClick={() => {
                                                        if (isResponseClosed(ev)) return;

                                                        if (savedResponses[ev.id] && !editingResponses[ev.id]) {
                                                            setResponseMsg("");
                                                            setEditingResponses((prev) => ({
                                                                ...prev,
                                                                [ev.id]: true,
                                                            }));
                                                            return;
                                                        }

                                                        submitResponse(ev);
                                                    }}
                                                >
                                                    {savedResponses[ev.id] && !editingResponses[ev.id]
                                                        ? "Edit Response"
                                                        : "Save Response"}
                                                </button>
                                            </div>

                                            {eventResponses[ev.id]?.length > 0 && (
                                                <>
                                                    <select
                                                        className="response-list-select"
                                                        value={responseListViews[ev.id] || "attend"}
                                                        onClick={() =>
                                                            setResponseListViews((prev) => ({
                                                                ...prev,
                                                                [ev.id]: prev[ev.id] || "attend",
                                                            }))
                                                        }
                                                        onChange={(e) =>
                                                            setResponseListViews((prev) => ({
                                                                ...prev,
                                                                [ev.id]: e.target.value,
                                                            }))
                                                        }
                                                    >
                                                        {RESPONSE_LIST_OPTIONS.map((option) => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    {responseListViews[ev.id] && (() => {
                                                        const selectedStatus = responseListViews[ev.id];
                                                        const selectedList = RESPONSE_LIST_OPTIONS.find((option) => option.value === selectedStatus);
                                                        const rows = eventResponses[ev.id].filter((row) => row.status === selectedStatus);

                                                        return (
                                                        <div className="event-response-list">
                                                            <>
                                                                        <strong>{selectedList?.title || "Confirmed List"}</strong>
                                                                        {rows.length === 0 ? (
                                                                            <div className="event-response-row">
                                                                                <span>{selectedList?.empty || "No members yet."}</span>
                                                                            </div>
                                                                        ) : (
                                                                            rows.map((row) => (
                                                                                <div key={`${row.primaryid}-${row.updated_at}`} className="event-response-row">
                                                                                    <span>
                                                                                        {row.firstname} {row.lastname}
                                                                                    </span>
                                                                                </div>
                                                                            ))
                                                                        )}
                                                            </>
                                                        </div>
                                                        );
                                                    })()}
                                                </>
                                            )}

                                            {responseMsg && <div className="calendar-response-message">{responseMsg}</div>}
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>

                        <button
                            className="btn-secondary"
                            onClick={closeSelectedDay}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
